// Renders a VinSolutions remote built against a DIFFERENT React major, in its own React root
// bound to an isolated share scope.
//
// WHY this exists, the constraints it imposes on callers (no context crosses the boundary), the
// deploy-side prerequisites, and why it is excluded from coverage: see MODULE_FEDERATION.md at the
// repo root. Read it before changing this file.
import type { ComponentType } from 'react';
import { useEffect, useRef, useState } from 'react';

import { importModule } from './moduleImport';
import { getRemoteUrlDefinitions, loadManifest, setRemoteDefinitions } from './moduleLoader';

const HOST_PROVIDER_REMOTE = 'crw';

type IslandRuntime = {
    scope: Record<string, any>;
    React: any;
    createRoot: (el: Element) => { render: (node: any) => void; unmount: () => void };
    getExpose: (remoteName: string, exposeName: string) => Promise<any>;
};

let runtimePromise: Promise<IslandRuntime> | null = null;

// ATTEMPTED, not completed — a half-finished init must not be retried.
// See MODULE_FEDERATION.md, "Retryability".
let providerContainerInitialised = false;

/** Normalise anything thrown into an Error, so every catch site reports the same shape. */
const asError = (thrown: unknown): Error => (thrown instanceof Error ? thrown : new Error(String(thrown)));

// FIRST mark wins: a throw site knows its own step, its layer only supplies a default.
// Unmarked means not retryable. See MODULE_FEDERATION.md, "Retryability".
const RETRYABLE = Symbol('federatedIsland.retryable');

const markRetryable = (thrown: unknown, retryable: boolean): Error => {
    const error = asError(thrown) as Error & { [RETRYABLE]?: boolean };
    if (error[RETRYABLE] === undefined) error[RETRYABLE] = retryable;
    return error;
};

/** A failure that repeating cannot change — it describes the deployed artifact, not a transient. */
const permanent = (message: string): Error => markRetryable(new Error(message), false);

/*
  How the module-federation runtime words a TRANSIENT load failure. Read out of the shipped
  remoteEntry.js rather than guessed, and used as an allow-list: anything else coming out of a
  container is treated as PERMANENT.

  That direction is deliberate. Telling someone to reload when a retry would have worked costs one
  reload; offering a retry that cannot work never resolves, which is the affordance defect this
  classification exists to avoid. So an unrecognised wording — a runtime upgrade rephrasing its
  errors — degrades to the harmless side.
*/
const TRANSIENT_LOAD_FAILURE =
    /ScriptNetworkError|LinkNetworkError|Loading (?:CSS )?chunk .*failed|Failed to load script|Failed to fetch|NetworkError/i;

const isTransient = (error: unknown): boolean => TRANSIENT_LOAD_FAILURE.test(asError(error).message);

const isRetryable = (error: Error): boolean => (error as Error & { [RETRYABLE]?: boolean })[RETRYABLE] === true;

/**
 * A sortable key for a share-scope version, highest-first when sorted descending as a string.
 *
 * The prerelease suffix MUST be split off before the core is parsed — `parseInt('0-rc')` is 0, not
 * NaN. See MODULE_FEDERATION.md, "Version selection in the share scope".
 */
const versionSortKey = (version: string): string => {
    const dash = version.indexOf('-');
    const [core, prerelease] = dash === -1 ? [version, ''] : [version.slice(0, dash), version.slice(dash + 1)];
    const padded = core.split('.').map((segment) => String(Number.parseInt(segment, 10) || 0).padStart(6, '0'));
    while (padded.length < 4) padded.push('000000');
    return `${padded.slice(0, 4).join('.')}|${prerelease ? `0${prerelease}` : '1'}`;
};

/**
 * Pick the version key to resolve for `name`: `loaded` first, then the highest version.
 *
 * NOT `Object.keys(versions)[0]` — that is insertion order. See MODULE_FEDERATION.md,
 * "Version selection in the share scope".
 */
const selectVersion = (versions: Record<string, any>): string => {
    const keys = Object.keys(versions);
    return keys.find((key) => versions[key]?.loaded) ?? keys.sort((a, b) => (versionSortKey(a) < versionSortKey(b) ? 1 : -1))[0];
};

/** Resolve a module out of a share scope. `entry.lib` can exist but be EMPTY — always use get(). */
const resolveShared = async (scope: Record<string, any>, name: string) => {
    const versions = scope[name];
    // `{}` is truthy but has no version to select, which would surface as a TypeError instead of
    // this diagnostic. See MODULE_FEDERATION.md, "Version selection in the share scope".
    if (!versions || Object.keys(versions).length === 0) {
        throw permanent(`Isolated scope has no usable "${name}" (it carries: ${Object.keys(scope).join(', ')})`);
    }
    const version = selectVersion(versions);
    const got = await versions[version].get();
    return { module: typeof got === 'function' ? got() : got, version };
};

const resolveRemoteBaseUrl = async (remoteName: string): Promise<string> => {
    let definitions = getRemoteUrlDefinitions();
    if (!definitions) {
        definitions = await loadManifest();
        setRemoteDefinitions(definitions);
    }
    const url = definitions[remoteName];
    if (!url) {
        // Permanent: `setRemoteDefinitions` has cached this map, so re-reading it cannot differ.
        throw permanent(`Module federation manifest has no entry for remote "${remoteName}"`);
    }
    return url;
};

// `?island` forces a fresh container instance. See MODULE_FEDERATION.md, "`?island` on the container URL".
const importContainer = async (remoteName: string) => {
    const base = await resolveRemoteBaseUrl(remoteName);
    const url = `${base}${base.endsWith('/') ? '' : '/'}remoteEntry.js?island`;
    const container: any = await importModule(url);
    if (typeof container.init !== 'function') {
        // Permanent: the import SUCCEEDED, so the ESM registry has cached this module and a retry
        // re-imports the same non-container. This is the wrong-artifact case — remoteEntry.js served
        // as an HTML error page with a 200, or a non-MF build deployed to that path.
        throw permanent(`Remote "${remoteName}" is not an ESM module-federation container (no init)`);
    }
    return container;
};

// EVERY init failure throws, including already-initialised. Callers must mark the container
// init-attempted BEFORE awaiting this, never after. See MODULE_FEDERATION.md, "Retryability".
const initIntoScope = async (remoteName: string, container: any, scope: Record<string, any>) => {
    try {
        await container.init(scope);
    } catch (error) {
        const { message } = asError(error);
        if (/already been initialized/i.test(message)) {
            throw permanent(
                `Remote "${remoteName}" was already initialised against another share scope, so its modules ` +
                    `would be bound to the wrong React. This usually means another code path loaded the same ` +
                    `container URL first. Original error: ${message}`
            );
        }
        throw error;
    }
};

const getRuntime = (): Promise<IslandRuntime> => {
    if (runtimePromise) return runtimePromise;

    runtimePromise = (async (): Promise<IslandRuntime> => {
        // A FRESH scope, deliberately NOT __webpack_share_scopes__.default.
        const scope: Record<string, any> = {};

        const provider = await importContainer(HOST_PROVIDER_REMOTE);
        // Set BEFORE the await, not after: see initIntoScope. Everything up to this point (the
        // manifest fetch, the container import) leaves nothing behind and is worth retrying;
        // everything from here on is not.
        providerContainerInitialised = true;
        await initIntoScope(HOST_PROVIDER_REMOTE, provider, scope);
        if (!scope.react) {
            throw permanent(
                `"${HOST_PROVIDER_REMOTE}" did not provide React into the isolated scope (it carries: ` +
                    `${Object.keys(scope).join(', ') || 'nothing'}). It must be built as a module-federation ` +
                    `HOST (eager provider) for this to work.`
            );
        }

        const { module: React, version: reactVersion } = await resolveShared(scope, 'react');
        // Both, because the guest error boundary extends `Component`. Checked here rather than at
        // first use so a broken scope reports as one. See MODULE_FEDERATION.md, "The guest error boundary".
        const missing = ['createElement', 'Component'].filter((key) => typeof React?.[key] !== 'function');
        if (missing.length > 0) {
            throw permanent(`Isolated scope resolved "react" ${reactVersion} to something with no ${missing.join(' or ')}`);
        }
        // React 19 removed createRoot from "react-dom", so react-dom/client is the only source.
        const { module: client } = await resolveShared(scope, 'react-dom/client');
        if (typeof client?.createRoot !== 'function') {
            throw permanent('Isolated scope has react-dom/client but no createRoot');
        }
        // Logged once per page. Which React the island actually bound to is the first thing anyone
        // needs when triaging a #31, and it is otherwise invisible from the outside.
        // eslint-disable-next-line no-console -- one-time diagnostic, see comment above
        console.info(
            `[federated-island] isolated scope ready: React ${reactVersion} from "${HOST_PROVIDER_REMOTE}" ` +
                `(runtime reports ${React.version ?? 'unknown'})`
        );

        const containers = new Map<string, Promise<any>>();
        const initAttempted = new Set<string>();
        const getExpose = async (remoteName: string, exposeName: string) => {
            if (!containers.has(remoteName)) {
                const pending = (async () => {
                    const container = await importContainer(remoteName);
                    initAttempted.add(remoteName);
                    // SAME isolated scope, so the remote consumes the React `crw` provided.
                    await initIntoScope(remoteName, container, scope);
                    return container;
                })();
                // Same rule as `providerContainerInitialised`, keyed on whether init was reached
                // rather than on our own error text. See MODULE_FEDERATION.md, "Retryability".
                const guarded = pending.catch((error: unknown) => {
                    const retryable = !initAttempted.has(remoteName);
                    if (retryable) containers.delete(remoteName);
                    throw markRetryable(error, retryable);
                });
                containers.set(remoteName, guarded);
            }
            const container = await containers.get(remoteName);
            let factory: () => any;
            try {
                factory = await container.get(exposeName);
            } catch (error) {
                // Retryable ONLY if the runtime names it a transient load failure. The other thing
                // `get` rejects with is `Module "./X" does not exist in container.` — a renamed
                // expose or a typo at the call site, which no retry clears.
                throw markRetryable(error, isTransient(error));
            }
            try {
                // `factory()` evaluates the exposed module's own top-level code, so a throw there is
                // a property of the deployed artifact. Awaited and classified the same way rather
                // than assumed synchronous: today's build resolves a sync factory from `get`, but a
                // top-level await would make it async and a transient failure could surface here.
                return await factory();
            } catch (error) {
                throw markRetryable(error, isTransient(error));
            }
        };

        return { scope, React, createRoot: client.createRoot, getExpose };
    })();

    runtimePromise = runtimePromise.catch((error: unknown) => {
        // Retryable only before init; rethrown with the verdict so the error UI can offer a retry
        // only where one could work. See MODULE_FEDERATION.md, "Retryability".
        const retryable = !providerContainerInitialised;
        if (retryable) runtimePromise = null;
        throw markRetryable(error, retryable);
    });
    return runtimePromise;
};

type CacheEntry =
    | { status: 'pending'; promise: Promise<void> }
    | { status: 'resolved'; runtime: IslandRuntime; Component: any }
    | { status: 'rejected'; error: Error; retryable: boolean };

const moduleCache = new Map<string, CacheEntry>();

/*
  One retry per island per page load. A failure that survives a retry is repeating, so it is reported
  as permanent from then on however its layer classified it — the wording alone cannot separate a CDN
  blip from a stale remoteEntry.js against pruned chunks, but repeating it can.
  See MODULE_FEDERATION.md, "Retryability".
*/
const retryUsed = new Set<string>();

const hasRetryBudget = (key: string): boolean => !retryUsed.has(key);

const consumeRetry = (key: string): void => {
    retryUsed.add(key);
};

/*
  Resolve the runtime AND the exposed component before the island renders anything, cached per
  remote+expose. A pending entry throws its promise, so the nearest <Suspense> shows its fallback —
  the same contract as lazy(). See MODULE_FEDERATION.md, "Suspending, and the module cache".
*/
const loadIslandModule = (remoteName: string, exposeName: string): CacheEntry => {
    const key = `${remoteName}${exposeName}`;
    const cached = moduleCache.get(key);
    if (cached) return cached;

    const promise = (async () => {
        const runtime = await getRuntime();
        const mod = await runtime.getExpose(remoteName, exposeName);
        const Component = mod?.default ?? mod;
        // `typeof null === 'object'`, so null needs excluding explicitly.
        if (typeof Component !== 'function' && (typeof Component !== 'object' || Component === null)) {
            throw permanent(`"${key}" did not expose a component`);
        }
        moduleCache.set(key, { status: 'resolved', runtime, Component });
    })().catch((thrown: unknown) => {
        const error = asError(thrown);
        // eslint-disable-next-line no-console -- the console is the only channel that keeps the real cause
        console.error(`[federated-island] loading ${key} FAILED:`, error);
        moduleCache.set(key, { status: 'rejected', error, retryable: isRetryable(error) && hasRetryBudget(key) });
    });

    const entry: CacheEntry = { status: 'pending', promise };
    moduleCache.set(key, entry);
    return entry;
};

/*
  An error boundary built from the GUEST React, memoised per runtime. A throw in one React root does
  not propagate into another, so `onError` is the bridge back to the host component.
  See MODULE_FEDERATION.md, "The guest error boundary".
*/
const boundariesByRuntime = new WeakMap<object, any>();
const getIslandBoundary = (React: any) => {
    const cached = boundariesByRuntime.get(React);
    if (cached) return cached;

    const Base: new (...args: any[]) => any = React.Component;
    class IslandErrorBoundary extends Base {
        static getDerivedStateFromError() {
            return { failed: true };
        }
        constructor(...args: any[]) {
            super(...args);
            this.state = { failed: false };
        }
        componentDidCatch(error: unknown) {
            this.props.onError(asError(error));
        }
        render() {
            return this.state.failed ? null : this.props.children;
        }
    }

    boundariesByRuntime.set(React, IslandErrorBoundary);
    return IslandErrorBoundary;
};

const shallowEqualProps = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every((k) => Object.is(a[k], b[k]));
};

/**
 * Build a React component that renders a cross-major VinSolutions remote in its own React root.
 *
 * Drop-in for `lazy(() => loadRemoteModule(remote, expose))`: same props, same exported shape, and
 * it suspends while loading, so call sites keep their existing `<Suspense>` boundary and fallback.
 */
export function createFederatedIsland<P extends object>(remoteName: string, exposeName: string): ComponentType<P> {
    const key = `${remoteName}${exposeName}`;

    const FederatedIsland = (props: P) => {
        // Every hook runs before the conditional throw below, so hook order is identical on every
        // render that completes. A suspended render is discarded by React, exactly like lazy().
        const elRef = useRef<HTMLDivElement | null>(null);
        const renderRef = useRef<((next: P) => void) | null>(null);
        const lastPropsRef = useRef<P | null>(null);
        const propsRef = useRef(props);
        propsRef.current = props;
        const [attempt, setAttempt] = useState(0);
        const [renderError, setRenderError] = useState<Error | null>(null);

        const entry = loadIslandModule(remoteName, exposeName);
        const resolved = entry.status === 'resolved' ? entry : null;

        useEffect(() => {
            const host = elRef.current;
            if (!resolved || !host) return undefined;
            const { runtime, Component } = resolved;

            /*
              A FRESH child element, never elRef.current itself, and `display: contents` so it adds
              no layout box. Created outside the try so `teardown` always has something to remove.
              See MODULE_FEDERATION.md, "Mount, teardown and layout".
            */
            const mountPoint = host.ownerDocument.createElement('div');
            mountPoint.style.display = 'contents';
            host.appendChild(mountPoint);

            let root: { render: (node: any) => void; unmount: () => void } | null = null;

            /*
              One teardown, registered on BOTH the success and the failure path — returning without
              it from the catch leaked the root and falsified the props-sync invariant. `root` is
              nullable because it is assigned last. See MODULE_FEDERATION.md, "Mount, teardown and layout".
            */
            const teardown = () => {
                renderRef.current = null;
                lastPropsRef.current = null;
                const doomedRoot = root;
                root = null;
                // Deferred one task: unmounting another root from the HOST's commit phase makes
                // React warn and defer anyway. See MODULE_FEDERATION.md, "Mount, teardown and layout".
                setTimeout(() => {
                    try {
                        doomedRoot?.unmount();
                    } catch {
                        // the island owns its subtree; a failed unmount must not break this tree
                    }
                    mountPoint.remove();
                }, 0);
            };

            try {
                // Boundary before root, so a failure building it cannot leave a live root behind.
                const Boundary = getIslandBoundary(runtime.React);
                const activeRoot = runtime.createRoot(mountPoint);
                root = activeRoot;

                renderRef.current = (next: P) => {
                    lastPropsRef.current = next;
                    activeRoot.render(
                        runtime.React.createElement(
                            Boundary,
                            { onError: setRenderError },
                            runtime.React.createElement(Component, next)
                        )
                    );
                };
                renderRef.current(propsRef.current);
            } catch (thrown) {
                /*
                  This does NOT catch the remote's own render failing — that arrives via `onError`.
                  It catches the synchronous path only. See MODULE_FEDERATION.md, "The guest error boundary".
                */
                // eslint-disable-next-line no-console -- the console is the only channel that keeps the real cause
                console.error(`[federated-island] mounting ${key} FAILED:`, asError(thrown));
                setRenderError(asError(thrown));
                teardown();
                return undefined;
            }

            return teardown;
            // `renderError` is a dep so a guest failure tears the guest root down.
            // See MODULE_FEDERATION.md, "Mount, teardown and layout".
        }, [resolved, attempt, renderError]);

        /*
          Props stay live across the boundary; context does not. No dependency array and a shallow
          compare instead — `[props]` is a fresh identity every host render.
          See MODULE_FEDERATION.md, "Mount, teardown and layout".
        */
        useEffect(() => {
            // Set and cleared together, so a non-null `last` guarantees a live renderer.
            const last = lastPropsRef.current;
            if (!last || shallowEqualProps(last as Record<string, unknown>, props as Record<string, unknown>)) return;
            (renderRef.current as (next: P) => void)(props);
        });

        if (entry.status === 'pending') throw entry.promise;

        // A guest render failure is retryable while the budget lasts; a load failure only where its
        // layer said so AND the budget lasts. See MODULE_FEDERATION.md, "Retryability".
        const failure = renderError
            ? { error: renderError, retryable: hasRetryBudget(key) }
            : entry.status === 'rejected'
            ? { error: entry.error, retryable: entry.retryable }
            : null;

        if (failure) {
            // Deliberately generic — the real cause goes to the console. See MODULE_FEDERATION.md, "The error UI".
            return (
                <div data-testid="federated-island-error" style={{ padding: 16 }}>
                    <div>This section could not be loaded.</div>
                    {failure.retryable ? (
                        <button
                            type="button"
                            onClick={() => {
                                consumeRetry(key);
                                moduleCache.delete(key);
                                setRenderError(null);
                                setAttempt((n) => n + 1);
                            }}
                        >
                            Try again
                        </button>
                    ) : (
                        // A post-init failure is only cleared by a reload.
                        <div data-testid="federated-island-error-reload">Reload the page to try again.</div>
                    )}
                </div>
            );
        }

        return <div ref={elRef} data-testid="federated-island" style={{ width: '100%', height: '100%' }} />;
    };

    FederatedIsland.displayName = `FederatedIsland(${remoteName}${exposeName})`;
    return FederatedIsland as ComponentType<P>;
}
